import { formatMoney } from 'util.currency'
import { EVENTS } from 'util.events'
import { loadProduct } from 'util.product-loader'

class ProductPackUpsell {
  constructor(panel) {
    this.panel = panel
    this.sectionId = panel.dataset.sectionId
    this.productId = panel.dataset.productId
    this.currentTitle = panel.dataset.currentTitle || ''
    this.moneyFormat = panel.dataset.moneyFormat
    this.rootUrl = window.Shopify?.routes?.root || '/'
    this.collectionHandles = Array.from(
      new Set(
        `${panel.dataset.packCollections || ''}`
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean)
          .concat(['mini-doughnuts'])
      )
    )
    this.currentPriceEl = panel.querySelector('[data-pack-current-price]')
    this.targetTitleEl = panel.querySelector('[data-pack-target-title]')
    this.targetPriceEl = panel.querySelector('[data-pack-target-price]')
    this.deltaTextEl = panel.querySelector('[data-pack-delta-text]')
    this.deltaChipEl = panel.querySelector('[data-pack-delta-chip]')
    this.imageEl = panel.querySelector('[data-pack-image]')
    this.linkEl = panel.querySelector('[data-pack-link]')
    const text = panel.dataset
    this.text = {
      target: text.textTarget || '12 mini doughnuts',
      loading: text.textLoading || 'Loading...',
      finding: text.textFinding || 'Finding the best 12 mini match for this flavour.',
      checking: text.textChecking || 'Checking 12 minis',
      deltaMore: text.textDeltaMore || 'Add only [price] for 12 mini doughnuts.',
      deltaLess: text.textDeltaLess || 'Switch to 12 mini doughnuts and save [price].',
      deltaSame: text.textDeltaSame || 'Swap to 12 mini doughnuts for the same price.',
      chipMore: text.textChipMore || 'Add only [price]',
      chipLess: text.textChipLess || 'Save [price]',
      chipSame: text.textChipSame || 'Same spend'
    }
    this.cache = new Map()
    this.collectionCache = new Map()
    this.requestToken = 0
    this.handleVariantChange = this.handleVariantChange.bind(this)
  }

  init() {
    if (this.panel.dataset.upsellInitialized === 'true') return
    this.panel.dataset.upsellInitialized = 'true'

    document.addEventListener(`${EVENTS.variantChange}:${this.sectionId}:${this.productId}`, this.handleVariantChange)

    this.updateForVariant({
      sku: this.panel.dataset.currentSku || '',
      price: Number(this.panel.dataset.currentPrice || 0)
    })
  }

  destroy() {
    document.removeEventListener(`${EVENTS.variantChange}:${this.sectionId}:${this.productId}`, this.handleVariantChange)
  }

  handleVariantChange({ detail }) {
    if (!detail?.variant) {
      this.hide()
      return
    }

    this.updateForVariant(detail.variant)
  }

  async updateForVariant(variant) {
    const currentSku = `${variant?.sku || ''}`.trim()
    const currentPrice = Number(variant?.price || 0)
    const targetSkus = this.deriveMiniSkuCandidates(currentSku)

    this.updateCurrentPrice(currentPrice)

    if (!currentSku || !targetSkus.length) {
      this.hide()
      return
    }

    const token = ++this.requestToken
    this.setLoading()

    try {
      const match = await this.resolveUpsellProduct(targetSkus, this.currentTitle)
      if (token !== this.requestToken) return

      if (!match?.variant?.id) {
        this.hide()
        return
      }

      this.renderMatch(match, currentPrice)
    } catch (error) {
      console.warn('12-pack upsell could not be resolved.', error)
      if (token === this.requestToken) this.hide()
    }
  }

  updateCurrentPrice(price) {
    if (!this.currentPriceEl) return
    this.currentPriceEl.textContent = this.formatPrice(price)
  }

  setLoading() {
    if (this.targetTitleEl) this.targetTitleEl.textContent = this.text.target
    if (this.targetPriceEl) this.targetPriceEl.textContent = this.text.loading
    if (this.deltaTextEl) this.deltaTextEl.textContent = this.text.finding
    if (this.deltaChipEl) this.deltaChipEl.textContent = this.text.checking
    if (this.linkEl) {
      this.linkEl.setAttribute('aria-disabled', 'true')
      this.linkEl.removeAttribute('href')
    }
  }

  renderMatch({ product, variant }, currentPrice) {
    const targetPrice = Number(variant.price || 0)
    const targetTitle = product?.title || this.text.target
    const targetUrl = `${this.rootUrl}products/${product.handle}?variant=${variant.id}`
    const imageSrc = this.getImageSrc(product, variant)
    const deltaMessage = this.getDeltaMessage(currentPrice, targetPrice)
    const deltaChip = this.getDeltaChip(currentPrice, targetPrice)

    if (this.targetTitleEl) this.targetTitleEl.textContent = targetTitle
    if (this.targetPriceEl) this.targetPriceEl.textContent = this.formatPrice(targetPrice)
    if (this.deltaTextEl) this.deltaTextEl.textContent = deltaMessage
    if (this.deltaChipEl) this.deltaChipEl.textContent = deltaChip
    if (this.linkEl) {
      this.linkEl.href = targetUrl
      this.linkEl.removeAttribute('aria-disabled')
    }

    if (this.imageEl && imageSrc) {
      this.imageEl.src = imageSrc
      this.imageEl.alt = targetTitle
    }

    this.panel.classList.remove('is-hidden')
  }

  hide() {
    this.panel.classList.add('is-hidden')
  }

  deriveMiniSkuCandidates(sku) {
    const source = `${sku || ''}`.trim()
    if (!source) return []

    const candidates = new Set()
    const variations = [
      source.replace(/\bregular\b/gi, 'mini'),
      source.replace(/\breg\b/gi, 'mini'),
      source.replace(/regular/gi, 'mini'),
      source.replace(/reg/gi, 'mini')
    ]

    variations.forEach((candidate) => {
      const normalizedCandidate = `${candidate || ''}`.trim()
      if (!normalizedCandidate) return
      if (normalizedCandidate.toLowerCase() === source.toLowerCase()) return
      if (!/mini/i.test(normalizedCandidate)) return
      candidates.add(normalizedCandidate)
    })

    return Array.from(candidates)
  }

  normalizeSku(value) {
    return `${value || ''}`.trim().toLowerCase()
  }

  compactSku(value) {
    return this.normalizeSku(value).replace(/[^a-z0-9]/g, '')
  }

  buildSkuSets(values = []) {
    const exact = new Set()
    const compact = new Set()

    values.forEach((value) => {
      const normalized = this.normalizeSku(value)
      if (!normalized) return
      exact.add(normalized)

      const compactValue = this.compactSku(normalized)
      if (compactValue) compact.add(compactValue)
    })

    return { exact, compact }
  }

  deriveMiniTitleQuery(title) {
    const source = `${title || ''}`.trim()
    if (!source) return ''

    return source
      .replace(/\bregular\b/gi, 'mini')
      .replace(/\b6\b/g, '12')
      .replace(/\s+/g, ' ')
      .trim()
  }

  async resolveUpsellProduct(targetSkus, currentTitle = '') {
    const skuSets = this.buildSkuSets(targetSkus)
    const cacheKey = Array.from(skuSets.exact).sort().join('|')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    let match = await this.findInCollections(skuSets)
    if (!match) {
      match = await this.findInPredictiveSearch(targetSkus, skuSets, currentTitle)
    }
    if (!match) {
      match = await this.findInCollectionHandle('all', skuSets, 8)
    }

    this.cache.set(cacheKey, match)
    return match
  }

  async findInCollections(skuSets) {
    for (const handle of this.collectionHandles) {
      const maxPages = handle === 'mini-doughnuts' ? 4 : 2
      const match = await this.findInCollectionHandle(handle, skuSets, maxPages)
      if (match) return match
    }

    return null
  }

  async findInCollectionHandle(handle, skuSets, maxPages = 1) {
    if (!handle) return null

    for (let page = 1; page <= maxPages; page += 1) {
      const cacheKey = `${handle}:${page}`
      let products = this.collectionCache.get(cacheKey)

      if (!products) {
        const params = new URLSearchParams()
        params.set('limit', '250')
        params.set('page', `${page}`)

        try {
          const response = await fetch(`${this.rootUrl}collections/${handle}/products.json?${params.toString()}`, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })

          if (!response.ok) {
            this.collectionCache.set(cacheKey, [])
            break
          }

          const payload = await response.json()
          products = Array.isArray(payload?.products) ? payload.products : []
          this.collectionCache.set(cacheKey, products)
        } catch (error) {
          this.collectionCache.set(cacheKey, [])
          break
        }
      }

      if (!products.length) break

      const match = this.findMatchingVariant(products, skuSets)
      if (match) return match
    }

    return null
  }

  async findInPredictiveSearch(targetSkus, skuSets, currentTitle = '') {
    const queries = Array.from(new Set([...targetSkus, this.deriveMiniTitleQuery(currentTitle)].filter(Boolean)))

    for (const query of queries) {
      const params = new URLSearchParams()
      params.set('q', query)
      params.set('resources[type]', 'product')
      params.set('resources[limit]', '8')
      params.set('resources[options][unavailable_products]', 'hide')
      params.set('resources[options][fields]', 'title,product_type,variants.sku')

      try {
        const response = await fetch(`${this.rootUrl}search/suggest.json?${params.toString()}`, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })

        if (!response.ok) continue

        const payload = await response.json()
        const suggestions = payload?.resources?.results?.products || []

        for (const suggestion of suggestions) {
          const handle = suggestion?.handle
          if (!handle) continue

          const product = await loadProduct(handle)
          const skuMatch = this.findMatchingVariant([product], skuSets)
          if (skuMatch?.variant?.id) return skuMatch

          const fallbackVariant =
            Array.isArray(product?.variants) && /mini/i.test(`${product?.title || ''}`)
              ? product.variants.find((candidate) => candidate?.available !== false)
              : null

          if (fallbackVariant && query === this.deriveMiniTitleQuery(currentTitle)) {
            return { product, variant: fallbackVariant }
          }
        }
      } catch (error) {
        // Keep trying other query fallbacks quietly.
      }
    }

    return null
  }

  findMatchingVariant(products, skuSets) {
    for (const product of products) {
      if (!Array.isArray(product?.variants)) continue

      const variant = product.variants.find((candidate) => {
        if (candidate.available === false) return false

        const exactSku = this.normalizeSku(candidate?.sku)
        const compactSku = this.compactSku(candidate?.sku)

        return skuSets.exact.has(exactSku) || (compactSku && skuSets.compact.has(compactSku))
      })

      if (variant) {
        return { product, variant }
      }
    }

    return null
  }

  getImageSrc(product, variant) {
    if (variant?.featured_image?.src) return variant.featured_image.src
    if (variant?.featured_image?.url) return variant.featured_image.url
    if (product?.featured_image) return product.featured_image
    if (Array.isArray(product?.images) && product.images.length) return product.images[0]
    return ''
  }

  getDeltaMessage(currentPrice, targetPrice) {
    const delta = targetPrice - currentPrice

    if (delta > 0) {
      return this.text.deltaMore.replace('[price]', this.formatPrice(delta))
    }

    if (delta < 0) {
      return this.text.deltaLess.replace('[price]', this.formatPrice(Math.abs(delta)))
    }

    return this.text.deltaSame
  }

  getDeltaChip(currentPrice, targetPrice) {
    const delta = targetPrice - currentPrice

    if (delta > 0) return this.text.chipMore.replace('[price]', this.formatPrice(delta))
    if (delta < 0) return this.text.chipLess.replace('[price]', this.formatPrice(Math.abs(delta)))
    return this.text.chipSame
  }

  formatPrice(price) {
    return formatMoney(price, this.moneyFormat, false)
  }
}

const panels = Array.from(document.querySelectorAll('[data-pack-upsell]'))

panels.forEach((panel) => {
  new ProductPackUpsell(panel).init()
})